import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Clock, Stack } from "@phosphor-icons/react";

const monYear = (iso) => {
    const m = /^(\d{4})-(\d{2})/.exec(iso || ""); if (!m) return "";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[Number(m[2]) - 1]} ${m[1]}`;
};

/**
 * v1.28.99 — Period tabs for the mobile portal. If the current party has
 * siblings in the same split_group_id, render one big tap-target tab per
 * period + a "Combined" tab that summarises across every period. Hidden
 * (returns null) when there's only one period, so non-split parties see
 * no chrome. Switching a tab hits `/portal/switch-period` which remaps
 * the customer user's party_id server-side; the whole app re-fetches
 * against the new context on the next render tick.
 */
export default function PeriodTabs() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [switching, setSwitching] = useState(false);
    const [showCombined, setShowCombined] = useState(false);

    useEffect(() => {
        if (user?.role !== "customer") return;
        api.get("/portal/periods").then((r) => setData(r.data)).catch(() => setData(null));
    }, [user?.party_id, user?.role]);

    if (!data || (data.periods || []).length < 2) return null;

    const switchTo = async (pid) => {
        if (pid === user?.party_id) return;
        setSwitching(true);
        try {
            await api.post("/portal/switch-period", { party_id: pid });
            toast.success("Switched period");
            // Rehydrate the user (which carries party_id).
            if (typeof refresh === "function") await refresh();
            // Force a re-render by reloading the current SPA route without a full page reload.
            window.dispatchEvent(new Event("micm-portal-period-changed"));
            setTimeout(() => window.location.reload(), 250);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not switch"); }
        finally { setSwitching(false); }
    };

    return (
        <div data-testid="portal-period-tabs" className="mb-3 rounded-xl border border-slate-200 bg-white p-2 shadow-sm sm:p-3">
            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <Stack size={12} /> Ledger periods for your account
            </div>
            <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1">
                {(data.periods || []).map((p) => {
                    const active = p.is_current && !showCombined;
                    const label = p.name;
                    const range = p.period_from && p.period_until
                        ? `${monYear(p.period_from)} → ${monYear(p.period_until)}`
                        : p.period_from
                            ? `${monYear(p.period_from)} Onwards`
                            : p.period_until
                                ? `Till ${monYear(p.period_until)}`
                                : "All time";
                    return (
                        <button
                            key={p.id}
                            data-testid={`portal-period-${p.id}`}
                            onClick={() => { setShowCombined(false); switchTo(p.id); }}
                            disabled={switching}
                            className={`min-w-[46vw] snap-start rounded-lg border p-3 text-left transition ${active ? "border-emerald-400 bg-emerald-50 shadow-inner" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        >
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-900">
                                <Clock size={12} className="text-slate-500" />
                                <span className="truncate">{label}</span>
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">{range}</div>
                            {active && <div className="mt-1 text-[10px] font-bold uppercase text-emerald-700">Current view</div>}
                        </button>
                    );
                })}
                {data.combined && (
                    <button
                        data-testid="portal-period-combined"
                        onClick={() => setShowCombined((v) => !v)}
                        className={`min-w-[46vw] snap-start rounded-lg border p-3 text-left transition ${showCombined ? "border-indigo-400 bg-indigo-50 shadow-inner" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    >
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-900">
                            <Stack size={12} className="text-slate-500" />Combined Summary
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Across every period</div>
                    </button>
                )}
            </div>
            {showCombined && data.combined && (
                <div data-testid="portal-combined-summary" className="mt-2 grid grid-cols-3 gap-2 rounded-lg border border-indigo-200 bg-indigo-50/40 p-2 text-center text-[11px]">
                    <div><div className="text-[9px] font-bold uppercase tracking-widest text-indigo-700">Opening</div><div className="font-mono text-sm font-bold text-slate-900">₹{Number(data.combined.opening_balance || 0).toLocaleString("en-IN")}</div></div>
                    <div><div className="text-[9px] font-bold uppercase tracking-widest text-indigo-700">Movement</div><div className="font-mono text-sm font-bold text-slate-900">₹{Number(data.combined.movement || 0).toLocaleString("en-IN")}</div></div>
                    <div><div className="text-[9px] font-bold uppercase tracking-widest text-indigo-700">Closing</div><div className={`font-mono text-sm font-bold ${data.combined.closing_balance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>₹{Number(data.combined.closing_balance || 0).toLocaleString("en-IN")}</div></div>
                </div>
            )}
        </div>
    );
}
